import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { TrendingUp, User, LogOut, CreditCard, BarChart3, Settings, PieChart, XCircle, ShieldCheck } from "lucide-react";

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    const isAdmin = user?.role === "admin";
    const subStatus = user?.subscription_status || "trial";
    const statusColor = subStatus === "active" ? "bg-[#00C805]/20 text-[#00C805] border-[#00C805]/30"
        : subStatus === "trial" ? "bg-[#007AFF]/20 text-[#007AFF] border-[#007AFF]/30"
        : "bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/30";

    return (
        <header className="glass-header sticky top-0 z-50 border-b border-white/10 flex-shrink-0" data-testid="navbar">
            <div className="flex items-center justify-between h-14 px-4 md:px-6">
                <Link to="/dashboard" className="flex items-center gap-2 transition-all hover:opacity-80" data-testid="nav-logo">
                    <BarChart3 className="w-6 h-6 text-[#007AFF]" />
                    <span className="text-lg font-bold tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        TradeNalysis
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    <Link to="/dashboard">
                        <Button variant="ghost" className="text-zinc-400 hover:text-white hover:bg-white/5 rounded-xl text-sm" data-testid="nav-dashboard">
                            <TrendingUp className="w-4 h-4 mr-1.5" /> Dashboard
                        </Button>
                    </Link>
                    {isAdmin && (
                        <Link to="/admin">
                            <Button variant="ghost" className="text-zinc-400 hover:text-white hover:bg-white/5 rounded-xl text-sm" data-testid="nav-admin">
                                <ShieldCheck className="w-4 h-4 mr-1.5" /> Admin
                            </Button>
                        </Link>
                    )}
                    {!isAdmin && (
                        <Link to="/pricing">
                            <Button variant="ghost" className="text-zinc-400 hover:text-white hover:bg-white/5 rounded-xl text-sm" data-testid="nav-pricing">
                                <CreditCard className="w-4 h-4 mr-1.5" /> Pricing
                            </Button>
                        </Link>
                    )}
                </nav>

                <div className="flex items-center gap-3">
                    <Badge variant="outline" className={`${statusColor} rounded-full text-[10px] tracking-widest uppercase font-bold border`} data-testid="subscription-badge">
                        {subStatus}
                    </Badge>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="rounded-xl border border-white/10 hover:bg-white/5 w-8 h-8" data-testid="user-menu-trigger">
                                <User className="w-4 h-4 text-zinc-400" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#0A0A0A] border-white/10 rounded-xl w-52">
                            <div className="px-3 py-2">
                                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
                                {isAdmin && <Badge className="mt-1 bg-[#007AFF]/20 text-[#007AFF] border-[#007AFF]/30 rounded-full text-[9px] px-2 py-0">Admin</Badge>}
                            </div>
                            <DropdownMenuSeparator className="bg-white/10" />
                            {isAdmin && (
                                <DropdownMenuItem onClick={() => navigate("/admin")} className="rounded-lg text-[#007AFF] hover:text-[#007AFF] focus:bg-[#007AFF]/10 focus:text-[#007AFF] cursor-pointer" data-testid="menu-admin">
                                    <ShieldCheck className="w-4 h-4 mr-2" /> Admin Dashboard
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => navigate("/settings")} className="rounded-lg text-zinc-400 hover:text-white focus:bg-white/5 focus:text-white cursor-pointer" data-testid="menu-settings">
                                <Settings className="w-4 h-4 mr-2" /> Settings
                            </DropdownMenuItem>
                            {!isAdmin && subStatus === "active" && (
                                <DropdownMenuItem onClick={() => navigate("/settings")} className="rounded-lg text-[#FF9F0A] hover:text-[#FF9F0A] focus:bg-[#FF9F0A]/10 focus:text-[#FF9F0A] cursor-pointer" data-testid="menu-cancel-sub">
                                    <XCircle className="w-4 h-4 mr-2" /> Cancel Subscription
                                </DropdownMenuItem>
                            )}
                            {!isAdmin && subStatus !== "active" && (
                                <DropdownMenuItem onClick={() => navigate("/pricing")} className="rounded-lg text-[#007AFF] hover:text-[#007AFF] focus:bg-[#007AFF]/10 focus:text-[#007AFF] cursor-pointer" data-testid="menu-subscribe">
                                    <CreditCard className="w-4 h-4 mr-2" /> Subscribe
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem onClick={handleLogout} className="rounded-lg text-[#FF3B30] hover:text-[#FF3B30] focus:bg-[#FF3B30]/10 focus:text-[#FF3B30] cursor-pointer" data-testid="menu-logout">
                                <LogOut className="w-4 h-4 mr-2" /> Logout
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </header>
    );
}
